// Bookie · Osmon Ko'k — preview thumbnail. The full design lives at
// /templates/bookie-azure.html and is now a CORPORATE CONFERENCE
// (Tech Summit Tashkent) re-skin — same Samarkand-tile azure #2C5C9F
// + paper #FBF6EC + Timurid gold #B89968 palette, same Persian-
// miniature iwan arch motif, Cinzel display. The thumbnail echoes
// that identity: gold/azure iwan silhouette, an eyebrow ("CORPORATE
// CONFERENCE"), the event title set in Cinzel, a small A·B·C
// 3-track strip, then the date — no couple ampersand, no Allura
// script, conference-formal.

import type { LayoutCtx } from "./_shared";

export function BookieAzurePreviewLayout({ names, date, venue, ink, bg, font }: LayoutCtx) {
  // For the conference re-skin `names` is a single event title.
  // Older mock data may still carry "A & B" — collapse gracefully.
  const eventName = names.replace(/\s*&\s*/g, " ").trim() || "Tech Summit";
  const azure = "#2C5C9F";
  const azureSoft = "#A8C0DE";
  const paper = "#FBF6EC";
  const paper2 = "#F2EBDA";
  const gold = "#B89968";
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: `linear-gradient(180deg, ${paper} 0%, ${paper2} 100%)`,
        color: ink,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        padding: "10% 9% 8%",
      }}
    >
      {/* Persian-miniature iwan arch — gold + azure arabesque silhouette. */}
      <svg
        aria-hidden
        viewBox="0 0 80 64"
        style={{ width: "48%", height: "auto", display: "block" }}
      >
        <path
          d="M14 60 L14 36 Q 14 20 40 6 Q 66 20 66 36 L66 60"
          fill="none"
          stroke={gold}
          strokeWidth="1"
          strokeLinejoin="round"
        />
        <path
          d="M19 60 L19 38 Q 19 23 40 11 Q 61 23 61 38 L61 60"
          fill="none"
          stroke={azure}
          strokeWidth="0.6"
          opacity="0.85"
        />
        <g fill={gold} opacity="0.92">
          <circle cx="40" cy="6" r="1.6" />
          <path d="M40 9 Q 38 13 36 14 Q 38 13 40 14 Q 42 13 44 14 Q 42 13 40 9 Z" />
        </g>
        <g fill="none" stroke={azure} strokeWidth="0.55" strokeLinecap="round" opacity="0.95">
          <path d="M28 52 Q 24 46 26 40 Q 30 36 34 40 Q 30 44 32 48" />
          <path d="M52 52 Q 56 46 54 40 Q 50 36 46 40 Q 50 44 48 48" />
        </g>
        <g fill={gold} opacity="0.9">
          <ellipse cx="26" cy="40" rx="1.4" ry="0.7" transform="rotate(-25 26 40)" />
          <ellipse cx="54" cy="40" rx="1.4" ry="0.7" transform="rotate(25 54 40)" />
          <ellipse cx="32" cy="48" rx="1.2" ry="0.6" transform="rotate(20 32 48)" />
          <ellipse cx="48" cy="48" rx="1.2" ry="0.6" transform="rotate(-20 48 48)" />
        </g>
        <path d="M40 16 L40 30" stroke={azure} strokeWidth="0.5" opacity="0.85" />
        <circle cx="40" cy="32" r="1.1" fill={azure} opacity="0.9" />
        <line x1="14" y1="60" x2="66" y2="60" stroke={azureSoft} strokeWidth="0.5" />
      </svg>

      {/* Eyebrow — conference identifier */}
      <div
        style={{
          fontFamily: "var(--font-ui)",
          fontSize: 7,
          letterSpacing: "0.45em",
          textTransform: "uppercase",
          color: gold,
          marginTop: 10,
          fontWeight: 600,
        }}
      >
        Corporate Conference
      </div>

      {/* Event title — Cinzel display, no ampersand split */}
      <div
        style={{
          fontFamily: "'Cinzel', serif",
          fontWeight: 600,
          fontSize: 16,
          lineHeight: 1.1,
          color: azure,
          marginTop: 10,
          letterSpacing: "0.06em",
          textTransform: "uppercase",
          maxWidth: "92%",
          textWrap: "balance" as const,
        }}
      >
        {eventName}
      </div>

      {/* Azure-gold rule */}
      <div
        aria-hidden
        style={{
          width: 38,
          height: 1,
          background: `linear-gradient(to right, transparent, ${gold}, ${azure}, ${gold}, transparent)`,
          margin: "10px 0 6px",
        }}
      />

      {/* 3-track marker */}
      <div
        style={{
          fontFamily: "'Cinzel', serif",
          fontWeight: 500,
          fontSize: 9,
          letterSpacing: "0.45em",
          color: azure,
          opacity: 0.92,
        }}
      >
        AI · PRODUCT · DESIGN
      </div>

      {/* Date + venue */}
      <div
        style={{
          marginTop: "auto",
          paddingTop: 10,
          fontFamily: "var(--font-ui)",
          fontSize: 7.5,
          letterSpacing: "0.32em",
          color: azure,
          opacity: 0.85,
          fontFeatureSettings: '"tnum" 1, "lnum" 1',
        }}
      >
        {date.replace(/\./g, " · ")}
      </div>
      <div
        style={{
          fontFamily: font,
          fontStyle: "italic",
          fontSize: 10,
          color: ink,
          opacity: 0.72,
          marginTop: 2,
        }}
      >
        {venue}
      </div>
    </div>
  );
}
